import { Link } from "react-router-dom";
import { withTeamDividers } from "./TeamDivider";
import { ArrowDown, ArrowUp } from "lucide-react";
import "./PlayerStatRow.css";
import { TeamTotalsRow } from "./TeamTotalsRow";
import { RecentLog } from "./RecentLog";
import { STATS, STAT_LABEL } from "@/lib/format";
import { FORM_DOTS } from "@/lib/preview";
import { useAuth } from "@/context/AuthContext";
import { dayLabel, formSlots, formTip } from "@/components/arena/arenaData";

// Admin override badge text / tooltip for players with a persistent availability override.
const OVR_TXT = { out: "OUT", active: "ΠΑΙΖΕΙ", starter: "ΒΑΣ." };
const OVR_LABEL = { out: "OUT — δεν παίζει", active: "Παίζει σίγουρα", starter: "Βασικός" };

const WHOLE = ["pts", "min", "pra"];
const fmt = (v, stat) => (v == null ? "—" : WHOLE.includes(stat) ? String(Math.round(v)) : Number(v).toFixed(1));

function Metric({ stat, row }) {
  if (row.mode === "final") {
    return <span className="psr-metric"><strong>{row.actual?.[stat] ?? "—"}</strong></span>;
  }
  const p = row.stats[stat];
  if (row.live) {
    return (
      <span className="psr-metric psr-live" title="Projection / live">
        <strong>{Math.round(p.proj)}<i>/</i><em className={(row.live[stat] ?? 0) > Math.round(p.proj) ? "over" : ""} data-testid={`live-${stat}-${row.player_id}`}>{row.live[stat] ?? 0}</em></strong>
      </span>
    );
  }
  return (
    <span className="psr-metric">
      <strong title={`80% range ${fmt(p.low, stat)}–${fmt(p.high, stat)}`}>{fmt(p.proj, stat)}</strong>
    </span>
  );
}

function MinCell({ row, onCheck, active }) {
  return (
    <div className={`psr-stat psr-min${onCheck ? " psr-clickable" : ""}${active ? " is-checked" : ""}`} role="cell"
      {...(onCheck ? { onClick: () => onCheck(row.player_id, "min"), "data-checkable": "1" } : {})}
      data-testid={`cell-min-${row.player_id}`}>
      {row.live ? (
        <span className="psr-metric psr-live" title="Season average / live">
          <strong>{row.min == null ? "—" : Math.round(row.min)}<i>/</i><em className={row.min != null && (row.live.min ?? 0) > Math.round(row.min) ? "over" : ""} data-testid={`live-min-${row.player_id}`}>{row.live.min ?? 0}</em></strong>
        </span>
      ) : (
        <span className="psr-metric">
          <strong>{row.min == null ? "—" : row.mode === "final" ? row.min : Math.round(row.min)}</strong>
        </span>
      )}
    </div>
  );
}

function Form({ row, stat = "pts" }) {
  if (FORM_DOTS) {
    const slots = formSlots(row, 6, "overall");
    // Compare each game with the number shown in this row's column (projection; MIN = season average).
    const shown = stat === "min" ? row.min : row.stats?.[stat]?.proj;
    const avg = shown == null ? null : Number(fmt(shown, stat));
    if (row.mode === "final" || !slots.length) return <span className="psr-muted">—</span>;
    return (
      <span className="psr-dots" data-testid={`form-dots-${row.player_id}`}>
        {avg != null && <b className="proj" data-tip={`Pre-game projection\n${STAT_LABEL[stat] || stat.toUpperCase()}`} data-testid={`form-proj-${row.player_id}`}>{Math.round(shown)}</b>}
        {slots.map((g, i) => (g.missing
          ? <b key={`m-${g.date}-${i}`} className="missing" data-tip={`${dayLabel(g.date)}\nDid not play / no box score`} data-testid={`form-missing-${row.player_id}-${i}`}>–</b>
          : <b key={`${g.date}-${i}`} className={avg == null || g[stat] === avg ? "even" : g[stat] > avg ? "up" : "down"} data-tip={formTip(g)}>{g[stat] ?? "–"}</b>))}
      </span>
    );
  }
  const f = row.form?.pts;
  if (row.mode === "final" || row.games < 5 || !f || f.pct == null) return <span className="psr-muted">—</span>;
  return <span className={`psr-form-badge ${f.pct < 0 ? "negative" : "positive"}`}>{Number(f.last5).toFixed(1)} {f.pct >= 0 ? "+" : ""}{f.pct.toFixed(1)}%</span>;
}

export function PlayerStatRow({ row, minGames, logStat, formStat, onCheck, check }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const matchup = `${row.team} ${row.home ? "vs" : "@"} ${row.opp}`;
  const injury = row.injury ? (/out/i.test(row.injury) ? "out" : "dtd") : null;
  const canCheck = onCheck && row.mode !== "final" ? onCheck : null;
  const checked = (s) => check && check.playerId === row.player_id && check.stat === s;
  const rotationOut = row.rotation_out && row.mode !== "final";  // expected 0 minutes: show the row disabled, no numbers
  return (
    <div className={`psr-row${rotationOut ? " psr-out-row" : ""}`} role="row" data-mode={row.mode} data-testid={`proj-row-${row.player_id}`}>
      <div className="psr-player" role="cell">
        <span className="psr-jersey" data-testid={`jersey-${row.player_id}`}>{row.jersey ?? "—"}</span>
        <div className={`psr-portrait ${row.euro ? "is-euro" : ""}`}>
          {row.photo ? <img src={row.photo} alt={row.name} loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <span className="psr-fallback">NBA</span>}
        </div>
        <div className="psr-identity">
          <div className="psr-name-line">
            <Link to={`/player/${row.player_id}?game=${row.game_id}`} data-testid={`player-link-${row.player_id}`}>{row.name}</Link>
            {row.team_logo && <img className="psr-team-logo" src={row.team_logo} alt={row.team} loading="lazy" />}
            {row.play_pct != null && row.play_pct < 90 && row.mode !== "final" && (
              <span className={`psr-pct ${row.play_pct >= 90 ? "hi" : row.play_pct >= 50 ? "mid" : "lo"}`} title="Chance to play (RotoWire)" data-testid={`play-pct-${row.player_id}`}>{row.play_pct}%</span>
            )}
            {injury && <span className={`psr-badge ${injury}`} data-testid={`injury-${row.player_id}`} title={row.injury}>{injury === "out" ? "OUT" : "DTD"}</span>}
            {isAdmin && row.override && OVR_TXT[row.override] && (
              <span className={`psr-badge ovr-${row.override}`} data-testid={`override-${row.player_id}`} title={`Χειροκίνητη ρύθμιση admin: ${OVR_LABEL[row.override]}`}>⚙ {OVR_TXT[row.override]}</span>
            )}
          </div>
          <div className="psr-subline"><b>{row.team}</b><span className="psr-separator">|</span><span>{matchup}</span></div>
          {logStat && <RecentLog recent={row.recent} stat={logStat} pid={row.player_id} />}
        </div>
      </div>
      {rotationOut ? (
        ["pts", "min", "reb", "ast", "fg3m", "pra"].map((s) => (
          <div className="psr-stat" role="cell" key={s} data-testid={`cell-${s}-${row.player_id}`}>
            <span className="psr-metric psr-muted">—</span>
          </div>
        ))
      ) : row.mode === "final" || row.stats ? STATS.flatMap((s) => [
        <div className={`psr-stat${canCheck ? " psr-clickable" : ""}${checked(s) ? " is-checked" : ""}`} role="cell" key={s}
          {...(canCheck ? { onClick: () => canCheck(row.player_id, s), "data-checkable": "1" } : {})}
          data-testid={`cell-${s}-${row.player_id}`}><Metric stat={s} row={row} /></div>,
        ...(s === "pts" ? [<MinCell key="min" row={row} onCheck={canCheck} active={checked("min")} />] : []),
      ]) : (
        <div className="psr-noproj" style={{ gridColumn: "span 6" }} role="cell" data-testid={`no-proj-${row.player_id}`}>Not enough games yet ({row.games}/{minGames}) — no projection</div>
      )}
      <div className="psr-form" role="cell">{rotationOut ? <span className="psr-muted">—</span> : <Form row={row} stat={formStat} />}</div>
    </div>
  );
}

const TOTAL_STATS = ["pts", "reb", "ast", "fg3m"];

function TotalsRow({ rows }) {
  const value = (r, s) => (r.mode === "final" ? r.actual?.[s] : r.stats?.[s]?.proj) ?? 0;
  const counted = rows.filter((r) => (r.mode === "final" || r.stats) && !r.rotation_out);
  const sum = (s) => counted.reduce((acc, r) => acc + value(r, s), 0);
  const final = counted.length > 0 && counted.every((r) => r.mode === "final");
  const show = (s) => (final || WHOLE.includes(s) ? Math.round(sum(s)) : sum(s).toFixed(1));
  return (
    <div className="psr-row psr-total" role="row" data-testid="totals-row">
      <div className="psr-player psr-total-label" role="cell">
        <div className="psr-total-title">Total</div><div className="psr-total-sub">{`${counted.length} players · ${final ? "actual" : "projected"}`}</div>
      </div>
      {["pts", "min", "reb", "ast", "fg3m", "pra"].map((s) => (
        <div key={s} className={`psr-stat ${s === "min" ? "psr-min" : ""}`} role="cell" data-testid={`total-${s}`}>
          {TOTAL_STATS.includes(s) && <span className="psr-metric"><strong>{show(s)}</strong></span>}
        </div>
      ))}
      <div className="psr-form" role="cell" />
    </div>
  );
}

const COLS = [{ key: "name", label: "Player" }, { key: "pts", label: "PTS" }, { key: "min", label: "MIN" },
  ...STATS.slice(1).map((s) => ({ key: s, label: STAT_LABEL[s] })), { key: "form", label: "Form L6" }];
const FORM_STATS = ["pts", "min", ...STATS.slice(1)];

export function PlayerStatsTable({ rows, sort, onSort, minGames, totals, logStat, teamDividers = false, onCheck, check }) {
  // FORM dots follow the stat column you sort by (PTS when sorting by name/form).
  const formStat = FORM_STATS.includes(sort.key) ? sort.key : "pts";
  return (
    <div className="psr-table-scroll">
      <div className="psr-table" data-testid="projections-table">
        <div className="psr-header">
          {COLS.map((c) => (
            <span key={c.key}>
              <button type="button" data-testid={`sort-${c.key}`} onClick={() => onSort(c.key)} className={sort.key === c.key ? "is-active" : ""}>
                {c.key === "form" ? `${c.label} · ${COLS.find((x) => x.key === formStat).label}` : c.label}{sort.key === c.key && (sort.dir === -1 ? <ArrowDown size={12} /> : <ArrowUp size={12} />)}
              </button>
            </span>
          ))}
        </div>
        {withTeamDividers(rows, (r) => <PlayerStatRow row={r} minGames={minGames} logStat={logStat} formStat={formStat} onCheck={onCheck} check={check} />, teamDividers)}
        {rows.length > 0 && totals !== false && (totals?.teamTotals ? <TeamTotalsRow rows={rows} {...totals} /> : <TotalsRow rows={rows} />)}
      </div>
    </div>
  );
}
